import { useState, useEffect } from "react";
import api, { extractError } from "@/api/client";
import { X, Send, Users, Bike, Megaphone, Check, AlertCircle, History, Clock } from "lucide-react";

/**
 * Admin broadcast composer. Sends a push notification to all customers and/or
 * drivers. Uses POST /admin/broadcast-notification.
 * Second tab shows the recent broadcast history from GET /admin/broadcast-history.
 */
export default function AdminBroadcastModal({ onClose }) {
  const [tab, setTab] = useState("send"); // send | history
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [link, setLink] = useState("");
  const [target, setTarget] = useState({ customer: true, driver: true });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [result, setResult] = useState(null);

  const [history, setHistory] = useState([]);
  const [histLoading, setHistLoading] = useState(false);

  useEffect(() => {
    if (tab !== "history") return;
    let cancel = false;
    setHistLoading(true);
    api.get("/admin/broadcast-history").then(({ data }) => {
      if (!cancel) setHistory(Array.isArray(data) ? data : []);
    }).catch(() => { if (!cancel) setHistory([]); })
      .finally(() => { if (!cancel) setHistLoading(false); });
    return () => { cancel = true; };
  }, [tab, result]);

  const toggle = (k) => setTarget((t) => ({ ...t, [k]: !t[k] }));

  const submit = async () => {
    setErr("");
    if (!title.trim() || !body.trim()) { setErr("العنوان والمحتوى مطلوبان"); return; }
    const roles = Object.keys(target).filter((k) => target[k]);
    if (roles.length === 0) { setErr("اختر جمهور واحد على الأقل"); return; }
    if (!window.confirm(`سيتم إرسال هذا الإشعار إلى ${roles.map((r) => r === "customer" ? "الزبائن" : "السائقين").join(" + ")}. متابعة؟`)) return;
    setBusy(true);
    try {
      const { data } = await api.post("/admin/broadcast-notification", {
        title: title.trim(),
        body: body.trim(),
        roles,
        link: link.trim() || undefined,
      });
      setResult(data);
    } catch (e) { setErr(extractError(e) || "فشل الإرسال"); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose} data-testid="broadcast-modal">
      <div onClick={(e) => e.stopPropagation()} className="bg-white w-full max-w-lg rounded-3xl shadow-2xl overflow-hidden flex flex-col" style={{maxHeight:"92vh"}}>
        <div className="flex justify-between items-center p-5 border-b border-[var(--border)] bg-gradient-to-l from-primary-brand to-[#1E3AB0] text-white">
          <div className="flex items-center gap-2">
            <Megaphone size={22}/>
            <h2 className="text-xl font-black" style={{fontFamily:'Cairo'}}>إشعار جماعي</h2>
          </div>
          <button onClick={onClose} className="w-8 h-8 rounded-full bg-white/20 hover:bg-white/30 flex items-center justify-center" data-testid="broadcast-close">
            <X size={16}/>
          </button>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-[var(--border)] bg-[var(--surface-alt)]">
          <button
            onClick={() => setTab("send")}
            className={`flex-1 py-3 font-bold text-sm flex items-center justify-center gap-2 transition ${tab === "send" ? "bg-white text-primary-brand border-b-2 border-primary-brand" : "text-[var(--text-secondary)]"}`}
            data-testid="broadcast-tab-send"
          >
            <Send size={14}/> إرسال جديد
          </button>
          <button
            onClick={() => setTab("history")}
            className={`flex-1 py-3 font-bold text-sm flex items-center justify-center gap-2 transition ${tab === "history" ? "bg-white text-primary-brand border-b-2 border-primary-brand" : "text-[var(--text-secondary)]"}`}
            data-testid="broadcast-tab-history"
          >
            <History size={14}/> السجل
          </button>
        </div>

        {tab === "history" ? (
          <div className="flex-1 overflow-auto p-4" data-testid="broadcast-history-list">
            {histLoading ? (
              <div className="text-center py-10 text-sm text-[var(--text-secondary)]">جارٍ التحميل...</div>
            ) : history.length === 0 ? (
              <div className="text-center py-10 text-sm text-[var(--text-secondary)]">
                <History size={32} className="mx-auto opacity-40 mb-2"/>
                لا يوجد سجل إشعارات بعد
              </div>
            ) : (
              <div className="space-y-2">
                {history.map((h) => (
                  <div key={h.id} className="bg-white rounded-2xl border border-[var(--border)] p-3 hover:border-primary-brand/40 transition" data-testid={`bh-item-${h.id}`}>
                    <div className="flex justify-between items-start gap-2 mb-1">
                      <div className="font-black text-sm flex-1" style={{fontFamily:'Cairo'}}>{h.title}</div>
                      <div className="text-[10px] text-[var(--text-secondary)] flex items-center gap-1 shrink-0">
                        <Clock size={10}/> {new Date(h.created_at).toLocaleString("ar-LY", { dateStyle: "short", timeStyle: "short" })}
                      </div>
                    </div>
                    <div className="text-xs text-[var(--text-secondary)] mb-2 whitespace-pre-wrap leading-relaxed">{h.body}</div>
                    <div className="flex flex-wrap items-center gap-1.5 text-[11px]">
                      <span className="bg-primary-light text-primary-brand px-2 py-0.5 rounded-full font-black">
                        {h.targeted ?? 0} مستلم
                      </span>
                      {Object.entries(h.per_role || {}).map(([r, n]) => (
                        <span key={r} className="bg-[var(--surface-alt)] text-[var(--text-secondary)] px-2 py-0.5 rounded-full font-bold">
                          {r === "customer" ? "زبائن" : "سائقين"}: {n}
                        </span>
                      ))}
                      {h.admin_name && (
                        <span className="text-[10px] text-[var(--text-secondary)] ml-auto">
                          بواسطة {h.admin_name}
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        ) : result ? (
          <div className="p-6 text-center">
            <div className="w-16 h-16 mx-auto rounded-full bg-green-100 flex items-center justify-center mb-3">
              <Check size={32} className="text-green-600"/>
            </div>
            <h3 className="text-lg font-black mb-2" style={{fontFamily:'Cairo'}}>تم الإرسال بنجاح</h3>
            <p className="text-sm text-[var(--text-secondary)] mb-2">
              وصل الإشعار إلى <b className="text-primary-brand">{result.targeted}</b> مستخدم عبر التطبيق (بانر فوري)
            </p>
            {typeof result.pushed_via_web_push === "number" && (
              <p className="text-xs text-[var(--text-secondary)] mb-4">
                منهم <b>{result.pushed_via_web_push}</b> استلموا إشعار Push (المستخدمون الذين فعّلوا الإشعارات على الجهاز)
              </p>
            )}
            <div className="grid grid-cols-2 gap-2 mb-5 text-sm">
              {Object.entries(result.per_role || {}).map(([r, n]) => (
                <div key={r} className="bg-[var(--surface-alt)] rounded-xl p-3">
                  <div className="text-xs text-[var(--text-secondary)]">{r === "customer" ? "الزبائن" : "السائقين"}</div>
                  <div className="font-black text-xl text-primary-brand" style={{fontFamily:'Cairo'}}>{n}</div>
                  {result.per_role_pushed && (
                    <div className="text-[10px] text-[var(--text-secondary)] mt-0.5">
                      Push: {result.per_role_pushed[r] || 0}
                    </div>
                  )}
                </div>
              ))}
            </div>
            <div className="flex gap-2">
              <button onClick={() => { setResult(null); setTitle(""); setBody(""); setLink(""); }} className="flex-1 py-3 rounded-xl bg-[var(--surface-alt)] text-[var(--text-secondary)] font-bold" data-testid="broadcast-new">
                إرسال آخر
              </button>
              <button onClick={onClose} className="flex-[2] py-3 rounded-xl bg-primary-brand text-white font-black" data-testid="broadcast-done">
                تم
              </button>
            </div>
          </div>
        ) : (
          <div className="p-5 space-y-4 overflow-auto">
            <div>
              <label className="text-xs font-bold text-[var(--text-secondary)] mb-1 block">العنوان</label>
              <input
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="مثال: تحديث جديد في التطبيق"
                maxLength={80}
                className="w-full p-3 rounded-xl border border-[var(--border)] focus:border-primary-brand focus:outline-none"
                data-testid="broadcast-title"
              />
            </div>
            <div>
              <label className="text-xs font-bold text-[var(--text-secondary)] mb-1 block">
                محتوى الإشعار <span className="text-[var(--text-secondary)]/70">({body.length}/280)</span>
              </label>
              <textarea
                value={body}
                onChange={(e) => setBody(e.target.value)}
                placeholder="اكتب رسالتك للمستخدمين..."
                maxLength={280}
                rows={4}
                className="w-full p-3 rounded-xl border border-[var(--border)] focus:border-primary-brand focus:outline-none resize-none"
                data-testid="broadcast-body"
              />
            </div>
            <div>
              <label className="text-xs font-bold text-[var(--text-secondary)] mb-1 block">رابط اختياري (يُفتح عند الضغط على الإشعار)</label>
              <input
                value={link}
                onChange={(e) => setLink(e.target.value)}
                placeholder="/ أو /orders"
                className="w-full p-3 rounded-xl border border-[var(--border)] focus:border-primary-brand focus:outline-none"
                data-testid="broadcast-link"
              />
            </div>
            <div>
              <label className="text-xs font-bold text-[var(--text-secondary)] mb-2 block">الجمهور المستهدف</label>
              <div className="grid grid-cols-2 gap-2">
                <button
                  onClick={() => toggle("customer")}
                  className={`p-3 rounded-xl border-2 transition font-bold text-sm flex items-center justify-center gap-2 ${target.customer ? "bg-primary-light border-primary-brand text-primary-brand" : "bg-white border-[var(--border)] text-[var(--text-secondary)]"}`}
                  data-testid="broadcast-target-customer"
                >
                  <Users size={16}/> الزبائن
                  {target.customer && <Check size={14}/>}
                </button>
                <button
                  onClick={() => toggle("driver")}
                  className={`p-3 rounded-xl border-2 transition font-bold text-sm flex items-center justify-center gap-2 ${target.driver ? "bg-primary-light border-primary-brand text-primary-brand" : "bg-white border-[var(--border)] text-[var(--text-secondary)]"}`}
                  data-testid="broadcast-target-driver"
                >
                  <Bike size={16}/> السائقين
                  {target.driver && <Check size={14}/>}
                </button>
              </div>
            </div>

            {err && (
              <div className="flex items-start gap-2 p-3 rounded-xl bg-red-50 border border-red-200 text-red-700 text-sm" data-testid="broadcast-err">
                <AlertCircle size={16} className="shrink-0 mt-0.5"/> {err}
              </div>
            )}

            <div className="flex gap-2">
              <button onClick={onClose} className="flex-1 py-3 rounded-xl bg-[var(--surface-alt)] text-[var(--text-secondary)] font-bold" data-testid="broadcast-cancel">
                إلغاء
              </button>
              <button
                onClick={submit}
                disabled={busy}
                className="flex-[2] py-3 rounded-xl bg-primary-brand text-white font-black flex items-center justify-center gap-2 disabled:opacity-60"
                data-testid="broadcast-send"
              >
                <Send size={16}/> {busy ? "جارٍ الإرسال..." : "إرسال الإشعار"}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
